import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE, SCREEN_ID, SCREEN_KEY, hasScreenCredentials } from '../../../config';

const POLL_INTERVAL = 5000;

export const OverlayManager = ({ children }) => {
  const [overlay, setOverlay] = useState(null);       // { type, text/url, id }
  const [phase, setPhase] = useState('dashboard');     // dashboard | fadeOut | overlay | fadeIn
  const lastOverlayId = useRef(null);
  const videoRef = useRef(null);

  // ─── Poll backend for this screen's scheduled overlay ───
  const poll = useCallback(async () => {
    // Without a paired screen id + device key there is nothing to poll.
    if (!hasScreenCredentials()) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/screens/${encodeURIComponent(SCREEN_ID)}/overlay`,
        { headers: { 'X-Screen-Key': SCREEN_KEY } }
      );
      if (!res.ok) return;

      const data = await res.json();

      if (data.type && data.id !== lastOverlayId.current) {
        // New overlay detected
        lastOverlayId.current = data.id;
        setOverlay(data);
        setPhase('fadeOut');
        setTimeout(() => setPhase('overlay'), 800);
      } else if (!data.type && phase === 'overlay') {
        // Overlay expired on server side
        endOverlay();
      }
    } catch {
      // Server not available, ignore
    }
  }, [phase]);

  useEffect(() => {
    const interval = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [poll]);

  // ─── End overlay → fade back to dashboard ───
  const endOverlay = () => {
    setPhase('fadeIn');
    setTimeout(() => {
      setOverlay(null);
      setPhase('dashboard');
    }, 800);
  };

  // ─── Video ended handler ───
  const handleVideoEnded = () => {
    endOverlay();
  };

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>

      {/* Dashboard content — hidden during overlay */}
      <div style={{
        opacity: phase === 'fadeOut' || phase === 'overlay' ? 0 : 1,
        transition: 'opacity 0.8s ease-in-out',
        width: '100%',
        height: '100%'
      }}>
        {children}
      </div>

      {/* Overlay layer */}
      <AnimatePresence>
        {(phase === 'overlay' || phase === 'fadeIn') && overlay && (
          <motion.div
            key={overlay.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === 'fadeIn' ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              background: '#000000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {overlay.type === 'message' && (
              <NeonTextOverlay text={overlay.text} />
            )}

            {overlay.type === 'video' && (
              <video
                ref={videoRef}
                src={`${API_BASE}${overlay.url}`}
                autoPlay
                muted
                onEnded={handleVideoEnded}
                onError={handleVideoEnded}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  background: '#000'
                }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════
//  Neon Text Overlay — word-by-word reveal
// ═══════════════════════════════════════════
const NeonTextOverlay = ({ text }) => {
  const words = text.split(' ');

  return (
    <div style={{
      padding: '60px',
      maxWidth: '80vw',
      textAlign: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '12px 16px'
    }}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{
            delay: i * 0.18,
            duration: 0.6,
            ease: 'easeOut'
          }}
          style={{
            display: 'inline-block',
            fontSize: 'clamp(36px, 6vw, 72px)',
            fontWeight: 600,
            color: '#ffffff',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            textShadow: `
              0 0 10px rgba(255,255,255,0.8),
              0 0 30px rgba(200,220,255,0.5),
              0 0 60px rgba(150,180,255,0.3),
              0 0 100px rgba(100,150,255,0.15)
            `,
            lineHeight: 1.2
          }}
        >
          {word}
        </motion.span>
      ))}
    </div>
  );
};
